import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Leaf, CheckCircle2 } from 'lucide-react';

const PrinterAnimation = () => {
  const [printProgress, setPrintProgress] = useState(0);
  const [currentLayer, setCurrentLayer] = useState(0);
  const [isPrinting, setIsPrinting] = useState(true);

  useEffect(() => {
    if (!isPrinting) return;
    
    const timer = setInterval(() => {
      setPrintProgress((prev) => {
        if (prev >= 100) {
          setIsPrinting(false);
          setTimeout(() => {
            setIsPrinting(true);
            setPrintProgress(0);
            setCurrentLayer(0);
          }, 3000);
          return 100;
        }
        return prev + 1;
      });
      setCurrentLayer((prev) => Math.min(prev + 1, 50));
    }, 80);

    return () => clearInterval(timer);
  }, [isPrinting]);

  const processSteps = [
    { step: '01', title: 'Plant-Based Material', desc: 'PLA pellets from renewable crops' },
    { step: '02', title: 'Precision 3D Printing', desc: 'Layer-by-layer fabrication at 180-220°C' },
    { step: '03', title: 'Electronics Assembly', desc: 'Sensor and circuit integration' },
    { step: '04', title: 'Quality Testing', desc: 'Rigorous performance validation' },
    { step: '05', title: 'Final Assembly', desc: 'Complete product packaging' },
  ];

  return (
    <div className="grid lg:grid-cols-2 gap-12 items-center">
      {/* 3D Printer Animation */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative aspect-square max-w-md mx-auto"
      >
        <div className="relative w-full h-full rounded-2xl bg-gradient-to-br from-gray-800/40 to-gray-900/60 border border-gray-700/50 overflow-hidden">
          <svg viewBox="0 0 400 400" className="w-full h-full">
            <defs>
              <linearGradient id="printerFrame" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#4a4a4a" />
                <stop offset="100%" stopColor="#2a2a2a" />
              </linearGradient>
              <linearGradient id="printBed" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#3a3a3a" />
                <stop offset="100%" stopColor="#1a1a1a" />
              </linearGradient>
              <linearGradient id="plaFilament" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#10b981" />
                <stop offset="100%" stopColor="#059669" />
              </linearGradient>
              <filter id="neonGlow">
                <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* Printer Frame - Vertical rails */}
            <rect x="70" y="60" width="10" height="280" rx="2" fill="url(#printerFrame)" />
            <rect x="320" y="60" width="10" height="280" rx="2" fill="url(#printerFrame)" />
            
            {/* Top bar */}
            <rect x="70" y="60" width="260" height="10" rx="2" fill="url(#printerFrame)" />
            
            {/* Print Bed */}
            <rect x="100" y="300" width="200" height="12" rx="2" fill="url(#printBed)" />
            <rect x="90" y="312" width="220" height="8" rx="2" fill="#1a1a1a" />
            
            {/* Grid lines on bed */}
            {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <line key={`gridH-${i}`} x1="100" y1={300 + i * 1.5} x2="300" y2={300 + i * 1.5} stroke="#4a4a4a" strokeWidth="0.5" />
            ))}
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
              <line key={`gridV-${i}`} x1={100 + i * 20} y1="300" x2={100 + i * 20} y2="312" stroke="#4a4a4a" strokeWidth="0.5" />
            ))}

            {/* Print Head Assembly */}
            <motion.g
              animate={{
                y: isPrinting ? [0, 5, 0, -5, 0] : 0,
                x: isPrinting ? [0, 30, 60, 30, 0, -30, -60, -30, 0] : 0,
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "linear"
              }}
            >
              {/* Horizontal carriage bar */}
              <rect x="90" y="100" width="220" height="8" rx="2" fill="url(#printerFrame)" />
              
              {/* Print head */}
              <rect x="180" y="105" width="40" height="50" rx="4" fill="#2a2a2a" stroke="#4a4a4a" strokeWidth="1" />
              
              {/* Heat block */}
              <rect x="188" y="145" width="24" height="15" rx="2" fill="#1a1a1a" />
              
              {/* Nozzle */}
              <polygon points="200,160 196,170 204,170" fill="#10b981" />
              
              {/* Hot nozzle glow when printing */}
              {isPrinting && (
                <motion.circle
                  cx="200"
                  cy="170"
                  r="4"
                  fill="#10b981"
                  filter="url(#neonGlow)"
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 0.5, repeat: Infinity }}
                />
              )}
              
              {/* Filament coming out */}
              {isPrinting && (
                <motion.line
                  x1="200"
                  y1="170"
                  x2="200"
                  y2="180"
                  stroke="#10b981"
                  strokeWidth="2"
                  strokeLinecap="round"
                  animate={{ opacity: [0.3, 1, 0.3] }}
                  transition={{ duration: 0.3, repeat: Infinity }}
                />
              )}

              {/* Cooling fan */}
              <rect x="220" y="110" width="15" height="15" rx="2" fill="#1a1a1a" stroke="#3a3a3a" strokeWidth="1" />
              <motion.circle
                cx="227.5"
                cy="117.5"
                r="5"
                fill="none"
                stroke="#4a4a4a"
                strokeWidth="1"
                strokeDasharray="3 3"
                animate={{ rotate: 360 }}
                transition={{ duration: 0.5, repeat: Infinity, ease: "linear" }}
              />
            </motion.g>

            {/* Filament spool */}
            <g>
              <ellipse cx="350" cy="100" rx="25" ry="25" fill="#1a1a1a" stroke="#10b981" strokeWidth="2" />
              <ellipse cx="350" cy="100" rx="10" ry="10" fill="#2a2a2a" />
              <motion.ellipse
                cx="350"
                cy="100"
                rx="25"
                ry="25"
                fill="none"
                stroke="#10b981"
                strokeWidth="4"
                strokeDasharray="10 5"
                animate={{ rotate: isPrinting ? 360 : 0 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              />
              <text x="350" y="140" textAnchor="middle" fill="#10b981" fontSize="8" fontWeight="500">PLA</text>
            </g>

            {/* Printed Object (Module being printed) */}
            <g>
              {/* Build up layers based on progress */}
              <clipPath id="printClip">
                <rect x="160" y={300 - (currentLayer * 2.5)} width="80" height="125" />
              </clipPath>
              
              <g clipPath="url(#printClip)">
                {/* Module shape being printed */}
                <rect x="170" y="175" width="60" height="125" rx="6" fill="url(#plaFilament)" opacity="0.9" />
                
                {/* Sensor cutouts */}
                <circle cx="185" cy="200" r="8" fill="#1a1a1a" />
                <circle cx="215" cy="200" r="8" fill="#1a1a1a" />
                <rect x="180" y="270" width="40" height="10" rx="2" fill="#1a1a1a" />
                
                {/* Layer lines effect */}
                {Array.from({ length: 50 }).map((_, i) => (
                  <line
                    key={`layer-${i}`}
                    x1="170"
                    y1={175 + i * 2.5}
                    x2="230"
                    y2={175 + i * 2.5}
                    stroke="rgba(0,0,0,0.1)"
                    strokeWidth="0.5"
                  />
                ))}
              </g>
            </g>

            {/* Progress indicator */}
            <g>
              <rect x="50" y="350" width="300" height="4" rx="2" fill="#1a1a1a" />
              <motion.rect
                x="50"
                y="350"
                width={printProgress * 3}
                height="4"
                rx="2"
                fill="url(#plaFilament)"
              />
              <text x="200" y="375" textAnchor="middle" fill="#6b7280" fontSize="10">
                {isPrinting ? `Printing... ${printProgress}%` : 'Complete!'}
              </text>
            </g>
          </svg>

          {/* Eco badge */}
          <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20">
            <Leaf className="w-3 h-3 text-emerald-400" />
            <span className="text-emerald-400 text-xs font-medium">Plant-Based PLA</span>
          </div>

          {/* Completion badge */}
          <AnimatePresence>
            {!isPrinting && printProgress === 100 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="absolute bottom-20 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/20 border border-emerald-500/40"
              >
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <span className="text-emerald-400 text-sm font-medium">Module Printed!</span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Process Steps */}
      <div className="space-y-4">
        <h3 className="text-xl font-semibold mb-6">Manufacturing Process</h3>
        {processSteps.map((item, index) => (
          <motion.div
            key={item.step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className="flex items-start gap-4"
          >
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
              (index + 1) * 20 <= printProgress
                ? 'bg-emerald-500/20 border-2 border-emerald-500/50'
                : 'bg-gray-800/50 border border-gray-700/50'
            }`}>
              <span className={`text-sm font-bold ${
                (index + 1) * 20 <= printProgress ? 'text-emerald-400' : 'text-gray-500'
              }`}>
                {item.step}
              </span>
            </div>
            <div className="pt-1">
              <h4 className={`font-medium transition-colors ${
                (index + 1) * 20 <= printProgress ? 'text-white' : 'text-gray-500'
              }`}>
                {item.title}
              </h4>
              <p className="text-gray-500 text-sm">{item.desc}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default PrinterAnimation;
